import z from "zod";

export const testSchema = z.object({
    title: z.string().min(1, 'Título é obrigatório'),
    reportName: z.string().min(1, 'Nome do relatório é obrigatório'),
    code: z.string().min(1, 'Código é obrigatório'),
    platform: z.enum(['mobile', 'desktop'], {
        errorMap: () => ({ message: 'Plataforma é obrigatória' }),
    }),
    allowedAttempts: z.number().min(1, 'Número de tentativas deve ser maior que 0'),
    assetCategory: z
        .array(z.string())
        .min(1, 'Pelo menos uma categoria do asset deve ser selecionada'),
    instrumentIds: z
        .array(z.string())
        .min(1, 'Pelo menos um instrumento deve ser selecionado'),
    annotations: z.array(z.string()).optional(),
    assetDisplayProperties: z
        .array(z.string())
        .min(1, 'Pelo menos uma propriedade deve ser selecionada'),
    steps: z
        .array(
            z
                .object({
                    title: z.string(),
                    stepType: z.enum(['form', 'camera', 'evidence', 'table'], {
                        errorMap: () => ({ message: 'Tipo do step é obrigatório' }),
                    }),
                    isRequired: z.boolean(),
                    isHidden: z.boolean().optional().default(false),
                    shouldValidate: z.boolean().optional().default(false),
                    durationInSeconds: z.number().nullable().optional(),
                    intervalInSeconds: z.number().nullable().optional(),
                    fields: z.any().optional().default([]),
                    subSteps: z.any().optional().default([]),
                    tableConfig: z
                        .object({
                            rowLabel: z.string().min(1, 'Rótulo da linha é obrigatório'),
                            rows: z.array(z.string()).min(1, 'Pelo menos 1 linha é obrigatória'),
                            columns: z.array(z.string()).min(1, 'Pelo menos 1 coluna é obrigatória'),
                        })
                        .optional(),
                })
                .refine(
                    (step) => {
                        // Validação do título - sempre obrigatório
                        if (!step.title || step.title.trim() === '') {
                            return false;
                        }

                        // Validações específicas para steps de câmera
                        if (step.stepType === 'camera') {
                            // Duração é obrigatória
                            if (!step.durationInSeconds || step.durationInSeconds <= 0) {
                                return false;
                            }
                            // Intervalo é obrigatório
                            if (!step.intervalInSeconds || step.intervalInSeconds <= 0) {
                                return false;
                            }
                            // Deve ter pelo menos 1 sub step com título e instrumentos
                            if (!step.subSteps || step.subSteps.length === 0) {
                                return false;
                            }
                            // Validar cada substep
                            for (const subStep of step.subSteps) {
                                if (!subStep.title || subStep.title.trim() === '') {
                                    return false;
                                }
                                if (!subStep.instrumentId || !Array.isArray(subStep.instrumentId) || subStep.instrumentId.length === 0) {
                                    return false;
                                }
                                if (!subStep.hints || subStep.hints.length === 0 || !subStep.hints.some(hint => hint.trim() !== '')) {
                                    return false;
                                }
                            }
                        }

                        // Validações específicas para steps de evidência
                        if (step.stepType === 'evidence') {
                            // Duração é obrigatória
                            if (!step.durationInSeconds || step.durationInSeconds <= 0) {
                                return false;
                            }
                            // Intervalo é obrigatório
                            if (!step.intervalInSeconds || step.intervalInSeconds <= 0) {
                                return false;
                            }
                            // Deve ter pelo menos 1 sub step com título (sem instrumentos)
                            if (!step.subSteps || step.subSteps.length === 0) {
                                return false;
                            }
                            // Validar cada substep (sem validação de instrumentId)
                            for (const subStep of step.subSteps) {
                                if (!subStep.title || subStep.title.trim() === '') {
                                    return false;
                                }
                                if (!subStep.hints || subStep.hints.length === 0 || !subStep.hints.some(hint => hint.trim() !== '')) {
                                    return false;
                                }
                            }
                        }

                        // Validações específicas para steps de formulário
                        if (step.stepType === 'form') {
                            // Deve ter pelo menos 1 campo
                            if (!step.fields || step.fields.length === 0) {
                                return false;
                            }
                            // Validar cada campo
                            for (const field of step.fields) {
                                if (!field.label || field.label.trim() === '') {
                                    return false;
                                }
                                if (!field.type || field.type.trim() === '') {
                                    return false;
                                }
                                if (field.type === 'select' && (!field.options || !field.options.some(option => option.trim() !== ''))) {
                                    return false;
                                }
                            }
                        }

                        // Validações específicas para steps de tabela
                        if (step.stepType === 'table') {
                            // Deve ter configuração de tabela
                            if (!step.tableConfig) {
                                return false;
                            }
                            // Deve ter pelo menos 1 linha
                            if (!step.tableConfig.rows || step.tableConfig.rows.length === 0) {
                                return false;
                            }
                            // Deve ter pelo menos 1 coluna
                            if (!step.tableConfig.columns || step.tableConfig.columns.length === 0) {
                                return false;
                            }
                        }

                        return true;
                    },
                    {
                        message:
                            'Steps de câmera e evidência devem ter duração, intervalo e pelo menos 1 sub step obrigatórios, steps de formulário devem ter pelo menos 1 campo, e steps de tabela devem ter pelo menos 1 linha e 1 coluna',
                        path: ['title'],
                    },
                ),
        )
        .min(1, 'Pelo menos um step é obrigatório'),
});
